// The one source of design tokens. Names and values are the shared spec
// (`_design-exp/DESIGN-TOKENS.md`) that the mobile app's `theme.ts` mirrors.
//
// Everything else derives from here: `tokensToCss` emits the CSS variables that
// `_variables.scss` points at, and `modules/theme/antdTheme.ts` feeds the same
// values to antd, whose algorithm needs real colours rather than `var()`.

// Light `primary`, `primaryHover`, `primaryActive`, `success` and `danger` are the lightest step of the
// original hue that still gives 4.5:1 (WCAG AA) both as a white label's fill and as text on the page
// grey (#f9f9fa); the brand #1b96ff was 3.06:1 on white. Hover and active go darker, not lighter, so
// the label keeps its contrast while pressed. `primaryText` is the link / selected-text blue and a
// shade darker, so it also clears 4.5:1 on the muted surface and on the primary tint.
// `textPlaceholder` is 4.5:1 on the page and the card, and `inputBorder` (the edge of an input) is 3:1
// there (WCAG 1.4.3 / 1.4.11). The same hex values live in the mobile app's theme; `border` stays
// faint on purpose, it is for cards.
export const COLOR_TOKENS = {
  light: {
    primary: '#0073d6',
    primaryHover: '#006bc7',
    primaryActive: '#005dad',
    primaryText: '#006dcb',
    onPrimary: '#ffffff',
    background: '#f9f9fa',
    surface: '#ffffff',
    surfaceMuted: '#f4f6f9',
    border: '#e8ebef',
    text: '#2e3a59',
    textMuted: '#626b83',
    textPlaceholder: '#6c7386',
    inputBorder: '#878d9e',
    textDisabled: '#8c93a3',
    danger: '#d82c00',
    success: '#368242',
    warning: '#faad14',
  },
  dark: {
    primary: '#3aa5ff',
    primaryHover: '#5cb6ff',
    primaryActive: '#1b96ff',
    primaryText: '#3aa5ff',
    onPrimary: '#0b1220',
    background: '#0f1115',
    surface: '#181b22',
    surfaceMuted: '#1f232c',
    border: '#2a2f3a',
    text: '#e6e9f0',
    textMuted: '#a1a8b8',
    textPlaceholder: '#858c9d',
    inputBorder: '#636a7a',
    textDisabled: '#566071',
    danger: '#ff7a57',
    success: '#7bd08a',
    warning: '#ffc53d',
  },
} as const;

export type ColorTokens = Record<keyof typeof COLOR_TOKENS.light, string>;

export const RADIUS_TOKENS = { sm: 6, md: 10, lg: 14, full: 999 } as const;

// Light lifts cards with a soft shadow; dark has none and relies on `border`.
export const SHADOW_TOKENS = {
  light: '0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 12px rgba(16, 24, 40, 0.06)',
  dark: 'none',
} as const;

export const SPACING_TOKENS = [4, 8, 12, 16, 20, 24, 32] as const;

export const FONT_FAMILY =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export const FONT_SIZE_TOKENS = {
  xs: 12,
  sm: 13,
  base: 15,
  md: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  '3xl': 32,
} as const;

// Motion, the same band as the mobile app's `duration` (ms). Feedback and small transitions stay in
// it so the UI never feels slow or holds up a click. Things enter on `out` (decelerate) and leave on
// `in` (accelerate), like the mobile sheets and modals (Easing.out / Easing.in of a cubic).
export const MOTION = {
  duration: { fast: 150, base: 200, slow: 250 },
  easing: {
    out: 'cubic-bezier(0.33, 1, 0.68, 1)',
    in: 'cubic-bezier(0.32, 0, 0.67, 0)',
    inOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
  },
  // The skeleton's slow breathing pulse is ambient "still loading" motion, not feedback, so it sits
  // outside the band on purpose (mobile Skeleton: 800 ms down to 45% opacity and back).
  skeletonPulse: { duration: 800, minOpacity: 0.45 },
} as const;

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const toDeclarations = (vars: Record<string, string | number>) =>
  Object.entries(vars)
    .map(([name, value]) => `--${name}:${value};`)
    .join('');

const colorVars = (colors: ColorTokens) => toDeclarations(colors);

// Derived, web only: a semantic colour used as TEXT on its own pale tint (tags, banners, shifts).
// The plain colours are 2.1 to 2.8:1 on their wash in light, so light mixes them toward `text`
// until they clear WCAG AA 4.5:1 on the tint (tokens.test.ts pins every pair); dark's colours are
// already light enough and stay as they are. `primaryText` is a real hex in COLOR_TOKENS because
// antd needs it for links. The danger share is 55% (was 65%, 4.47:1 on its tint) and success 70%.
const TEXT_ON_TINT = {
  light: {
    successText: 'color-mix(in srgb, var(--success) 70%, var(--text))',
    dangerText: 'color-mix(in srgb, var(--danger) 55%, var(--text))',
    warningText: 'color-mix(in srgb, var(--warning) 35%, var(--text))',
    // muted text on the neutral tint (the "Clock out" status tag) was 4.43:1
    neutralText: 'color-mix(in srgb, var(--textMuted) 80%, var(--text))',
  },
  dark: {
    successText: 'var(--success)',
    dangerText: 'var(--danger)',
    warningText: 'var(--warning)',
    neutralText: 'var(--textMuted)',
  },
} as const;

// Derived, web only: the keyboard focus ring must clear 3:1 (WCAG 1.4.11) on the page background
// and on cards. Light `primary` is 2.9:1 on the background, so light rings in `primaryActive`;
// dark `primary` already clears it.
const FOCUS_RING = {
  light: { focusRing: 'var(--primaryActive)' },
  dark: { focusRing: 'var(--primary)' },
} as const;

// Web only: the few accents that are not part of the shared spec (schedule shift variants, the
// violet link). A per-theme variable, not `light-dark()`, which Chrome before 123 and Safari before
// 17.5 do not parse (the whole declaration would be dropped).
const ACCENT_COLORS = {
  light: {
    violetText: '#6f00ff',
    shiftTimeOffOverlap: '#ffcf6e',
    shiftCustomBorder: '#7b61ff',
    shiftBlueBorder: '#2c86d4',
    shiftReviewBorder: '#c2255c',
  },
  dark: {
    violetText: '#a974ff',
    shiftTimeOffOverlap: '#7a5a1f',
    shiftCustomBorder: '#9d89ff',
    shiftBlueBorder: '#4ea1ec',
    shiftReviewBorder: '#f06595',
  },
} as const;

const MOTION_DURATION_VARS = {
  motionFast: MOTION.duration.fast,
  motionBase: MOTION.duration.base,
  motionSlow: MOTION.duration.slow,
};

const motionVars = () =>
  toDeclarations({
    ...Object.fromEntries(
      Object.entries(MOTION_DURATION_VARS).map(([name, ms]) => [name, `${ms}ms`]),
    ),
    easeOut: MOTION.easing.out,
    easeIn: MOTION.easing.in,
    easeInOut: MOTION.easing.inOut,
    skeletonPulseDuration: `${MOTION.skeletonPulse.duration}ms`,
    skeletonPulseOpacity: MOTION.skeletonPulse.minOpacity,
  });

// Every transition written with the duration variables becomes instant for people who asked their
// OS for less motion; the keyframe animations check the same query themselves.
const reducedMotionCss = () =>
  `@media ${REDUCED_MOTION_QUERY}{:root{${toDeclarations(
    Object.fromEntries(Object.keys(MOTION_DURATION_VARS).map(name => [name, '0ms'])),
  )}}}`;

const sharedVars = () =>
  toDeclarations({
    radiusSm: `${RADIUS_TOKENS.sm}px`,
    radiusMd: `${RADIUS_TOKENS.md}px`,
    radiusLg: `${RADIUS_TOKENS.lg}px`,
    radiusFull: `${RADIUS_TOKENS.full}px`,
    fontFamily: FONT_FAMILY,
    ...Object.fromEntries(
      Object.entries(FONT_SIZE_TOKENS).map(([name, size]) => [
        `fontSize-${name}`,
        `${size}px`,
      ]),
    ),
    ...Object.fromEntries(SPACING_TOKENS.map(size => [`space${size}`, `${size}px`])),
  });

/**
 * `:root` carries light (and everything theme independent); `[data-theme='dark']` overrides the
 * colours and the shadow. `color-scheme` makes native controls and scrollbars follow the theme.
 * The third block zeroes the motion durations under `prefers-reduced-motion`.
 */
export const tokensToCss = () =>
  [
    `:root{color-scheme:light;${sharedVars()}${motionVars()}${colorVars(
      COLOR_TOKENS.light,
    )}${toDeclarations(TEXT_ON_TINT.light)}${toDeclarations(FOCUS_RING.light)}${toDeclarations(
      ACCENT_COLORS.light,
    )}--shadow:${SHADOW_TOKENS.light};}`,
    `[data-theme='dark']{color-scheme:dark;${colorVars(COLOR_TOKENS.dark)}${toDeclarations(
      TEXT_ON_TINT.dark,
    )}${toDeclarations(FOCUS_RING.dark)}${toDeclarations(ACCENT_COLORS.dark)}--shadow:${
      SHADOW_TOKENS.dark
    };}`,
    reducedMotionCss(),
  ].join('\n');
